import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { currencyString, truncateString } from "@qgiv/core-js";
import {
    Divider,
    SummarySectionContainer,
    SummarySimpleItemOrSubtotalContainer,
} from "@qgiv/core-donor";
import ConnectedStoreCartList from "./ConnectedStoreCartList";

const TotalSummary = ({
    currency,
    gift = "",
    giftSubtotal,
    hasStoreCartItems,
    formattedPrePromoTotal = "",
    formattedStoreTax = "",
    processingFee = "",
    promoTotalDiscounts = [],
    storeSubtotal = "",
    registrationSubTotal = "",
    formattedTotal = "",
    isCms = false,
    isNfp,
    // Theme state
    giftAssistLabel,
    giftSubsectionHeading,
    optionalGiftLabel,
    optionalGiftLabelForNonFundraisingParticipants,
    totalLabel,
    subtotalLabel,
    storeSubsectionHeading,
    renderConnectedStoreCartList = () => {},
}) => {
    let giftContent;
    let promoTotalContent;
    let storeContent;
    let registrationSubTotalContent;

    if (storeSubtotal || hasStoreCartItems) {
        storeContent = (
            <SummarySectionContainer
                displayIcon
                iconGlyph="shopping-cart-solid"
                iconType="FontAwesome"
                sectionName={storeSubsectionHeading}
            >
                {isCms ? (
                    renderConnectedStoreCartList()
                ) : (
                    <ConnectedStoreCartList
                        formattedStoreTax={formattedStoreTax}
                        storeSubtotal={storeSubtotal}
                    />
                )}
                {storeSubtotal && (
                    <SummarySimpleItemOrSubtotalContainer
                        label={subtotalLabel}
                        subtotal={storeSubtotal}
                        isCms={isCms}
                        isBold
                        hasDivider
                        bottomMargin="20"
                    />
                )}
            </SummarySectionContainer>
        );
    }

    if (gift || processingFee) {
        giftContent = (
            <SummarySectionContainer
                displayIcon
                iconGlyph="gift-solid"
                iconType="FontAwesome"
                sectionName={giftSubsectionHeading}
            >
                {gift && (
                    <SummarySimpleItemOrSubtotalContainer
                        label={
                            isNfp
                                ? optionalGiftLabelForNonFundraisingParticipants
                                : optionalGiftLabel
                        }
                        subtotal={gift}
                        isCms={isCms}
                    />
                )}
                {processingFee && (
                    <>
                        {gift && (
                            <Divider
                                classNames={cx(
                                    isCms
                                        ? ""
                                        : "registration-summary__divider",
                                )}
                                dashed
                            />
                        )}

                        <SummarySimpleItemOrSubtotalContainer
                            label={giftAssistLabel}
                            subtotal={processingFee}
                        />
                    </>
                )}
                {/* Subtotal for entire gift section */}
                <SummarySimpleItemOrSubtotalContainer
                    label={subtotalLabel}
                    subtotal={giftSubtotal}
                    isCms={isCms}
                    isBold
                    hasDivider
                    bottomMargin="20"
                />
            </SummarySectionContainer>
        );
    }

    // TODO: This is for transaction level promo codes. We will need to make sure
    // this is styled correctly once we implement transaction level promos.
    // It might require an additional params or flags
    if (promoTotalDiscounts) {
        // eslint-disable-next-line array-callback-return, consistent-return
        promoTotalContent = promoTotalDiscounts.map((promo, i) => {
            if (promo.success) {
                return (
                    <SummarySimpleItemOrSubtotalContainer
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        label={truncateString(promo.name, 20)}
                        subtotal={`- ${currencyString(
                            promo.discountAmount,
                            currency,
                        )}`}
                        isPromo
                        bottomMargin="20"
                    />
                );
            }
        });
    }

    // Subtotal for all participants combined
    if (registrationSubTotal) {
        registrationSubTotalContent = (
            <SummarySimpleItemOrSubtotalContainer
                label={subtotalLabel}
                subtotal={registrationSubTotal}
                isBold
                hasDivider
                isCms={isCms}
                bottomMargin="20"
            />
        );
    }

    return (
        <>
            {formattedTotal && (
                // eslint-disable-next-line tailwindcss/classnames-order
                <div className="grid grid--no-gutters">
                    {registrationSubTotalContent}
                    {promoTotalContent}
                    {storeContent}
                    {giftContent}
                </div>
            )}
            {/* We need to account for a case were both registration and product are free */}
            {!formattedTotal && hasStoreCartItems && (
                // eslint-disable-next-line tailwindcss/classnames-order
                <div className="grid grid--no-gutters">{storeContent}</div>
            )}
            {formattedTotal && (
                // eslint-disable-next-line tailwindcss/classnames-order
                <div className="grid grid--no-gutters">
                    <div className="col col--12">
                        {/* eslint-disable-next-line tailwindcss/classnames-order */}
                        <div className="grid grid--no-left-gutter">
                            <div className="col">
                                <p>
                                    <strong className="-type--xlarge">
                                        {totalLabel}
                                    </strong>
                                </p>
                            </div>
                            <div className="col -text--right">
                                <p>
                                    <span className="-type--xlarge">
                                        {formattedPrePromoTotal &&
                                            formattedPrePromoTotal !==
                                                formattedTotal && (
                                                <span className="-text-decoration--line-through">
                                                    {formattedPrePromoTotal}
                                                </span>
                                            )}
                                        <span
                                            className={cx(
                                                formattedPrePromoTotal &&
                                                    formattedPrePromoTotal !==
                                                        formattedTotal &&
                                                    " -color--system-error -padding-left--5",
                                            )}
                                        >
                                            <strong>{formattedTotal}</strong>
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

TotalSummary.propTypes = {
    // These props should all be currency formatted numbers as strings
    gift: PropTypes.string,
    giftSubtotal: PropTypes.string.isRequired,
    processingFee: PropTypes.string,
    registrationSubTotal: PropTypes.string,
    formattedTotal: PropTypes.string,
    // Flag to tell us there is a store item if total is $0
    hasStoreCartItems: PropTypes.bool.isRequired,
    // Flag to tell us when this is being rendered in cms
    isCms: PropTypes.bool.isRequired,
    // Flag to tell us when primary registration is Nfp
    isNfp: PropTypes.bool.isRequired,
    // PromoCode specific props
    currency: PropTypes.shape({}).isRequired,
    formattedPrePromoTotal: PropTypes.string,
    promoTotalDiscounts: PropTypes.arrayOf(PropTypes.string),
    // Store specific props
    formattedStoreTax: PropTypes.string,
    storeSubtotal: PropTypes.string,
    // Theme Labels
    giftAssistLabel: PropTypes.string.isRequired,
    giftSubsectionHeading: PropTypes.string.isRequired,
    optionalGiftLabel: PropTypes.string.isRequired,
    optionalGiftLabelForNonFundraisingParticipants: PropTypes.string.isRequired,
    subtotalLabel: PropTypes.string.isRequired,
    totalLabel: PropTypes.string.isRequired,
    storeSubsectionHeading: PropTypes.string.isRequired,
    // CMS rendering of ConnectedStoreCartList
    renderConnectedStoreCartList: PropTypes.func,
};

export default TotalSummary;
